import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import {
  RequestParams,
  RequestAttribute,
} from '../../../utils/models/http.interface';

@Injectable({
  providedIn: 'root',
})
export class HttpService {
  private readonly apiUrl: string = environment.apiUrl;

  constructor(private readonly http: HttpClient) { }

  private buildHttpParams(requestParams?: RequestParams, attributes?: RequestAttribute[]): HttpParams | undefined {
    if (!requestParams && !attributes?.length) {
      return undefined;
    }

    let params = new HttpParams();

    if (requestParams) {
      if (requestParams.page !== undefined) {
        params = params.set('page', requestParams.page.toString());
      }
      if (requestParams.sort) {
        params = params.set('sort', requestParams.sort);
      }
      if (requestParams.limit !== undefined) {
        params = params.set('limit', requestParams.limit.toString());
      }
      if (requestParams.order) {
        params = params.set('order', requestParams.order);
      }
    }

    if (attributes) {
      attributes.forEach(att => {
        if (att.value) {
          params = params.append(att.param, att.value);
        }
      });
    }

    return params.keys().length > 0 ? params : undefined;
  }

  genericDownload<T>(endpoint: string, differentApi?) {
    const url = `${this.apiUrl}/${endpoint}`;

    let params = new HttpParams()
    const project = localStorage.getItem('project');
    if (project) {
      params = params.append('project', project);
    }

    return this.http.get(url, {
      responseType: 'blob',
      params
    });
  }

  genericDownloadNoProject(endpoint: string) {
    const url = `${this.apiUrl}/${endpoint}`;

    return this.http.get(url, { responseType: 'blob' });
  }

  genericGet<T>(endpoint: string, differentApi?): Observable<T> {
    const url = `${this.apiUrl}/${endpoint}`;

    let params = new HttpParams();
    const project = localStorage.getItem('project');
    if (project) {
      params = params.append('project', project);
    };

    return this.http.get<T>(url, { params }).pipe(map((response: T) => response));
  }

  genericGetNoProject<T>(endpoint: string, differentApi?): Observable<T> {
    const url = `${this.apiUrl}/${endpoint}`;

    let params = new HttpParams();

    return this.http.get<T>(url, { params }).pipe(map((response: T) => response));
  }

  genericGetSelectInfinite<T>(
    urlApi: string,
    endpoint: string,
    id?: string
  ): Observable<T> {
    const url = id ? `${urlApi}/${endpoint}/${id}` : `${urlApi}/${endpoint}`;

    let params = new HttpParams();
    const project = localStorage.getItem('project');
    if (project) {
      params = params.append('project', project);
    };

    return this.http.get<T>(url, { params }).pipe(map((response: T) => response));
  }

  genericGetWithId<T>(
    endpoint: string,
    id: string,
    attribute?: RequestAttribute[]
  ): Observable<T> {
    const url = `${this.apiUrl}/${endpoint}/${id}`;
    let params = new HttpParams();
    if (!!attribute) {
      attribute.forEach(
        (att) => (params = params.append(att.param, att.value))
      );
    }

    const project = localStorage.getItem('project');
    if (project) {
      params = params.append('project', project);
    };

    return this.http.get<T>(url, { params }).pipe(
      map((response: T) => {
        return response;
      })
    );
  }

  genericGetParams<T>(
    endpoint: string,
    requestParams?: RequestParams,
    attribute?: RequestAttribute[],
    differentApi?,
  ): Observable<T> {
    const url = `${this.apiUrl}/${endpoint}`;

    let params = new HttpParams()
      .set('limit', requestParams.limit?.toString())
      .set('page', requestParams.page?.toString())
    if (!!attribute) {
      attribute.forEach(
        (att) => (params = params.append(att.param, att.value))
      );
    }

    return this.http.get<T>(url, { params }).pipe(
      map((response: T) => {
        return response;
      })
    );
  }

  genericGetParamsProjects<T>(
    endpoint: string,
    requestParams?: RequestParams,
    attribute?: RequestAttribute[],
    differentApi?,
  ): Observable<T> {
    const url = `${this.apiUrl}/${endpoint}`;

    let params = new HttpParams()
      .set('limit', requestParams.limit?.toString())
      .set('page', requestParams.page?.toString())
    if (!!attribute) {
      attribute.forEach(
        (att) => (params = params.append(att.param, att.value))
      );
    }

    const project = localStorage.getItem('project');
    if (project) {
      params = params.append('project', project);
    };

    return this.http.get<T>(url, { params }).pipe(
      map((response: T) => {
        return response;
      })
    );
  }

  genericGetParamsTable<T>(
    endpoint: string,
    requestParams?: RequestParams,
    attribute?: RequestAttribute[],
    differentApi?,
  ): Observable<T> {
    const url = `${this.apiUrl}/${endpoint}`;

    let params = new HttpParams()
      .set('limit', requestParams.limit?.toString())
      .set('page', requestParams.page?.toString())
    if (!!attribute) {
      attribute.forEach(
        (att) => (params = params.append(att.param, att.value))
      );
    }

    const project = localStorage.getItem('id');
    if (project) {
      params = params.append('projectId', project);
    };

    return this.http.get<T>(url, { params }).pipe(
      map((response: T) => {
        return response;
      })
    );
  }

  genericGetParamsTableNameProject<T>(
    endpoint: string,
    requestParams?: RequestParams,
    attribute?: RequestAttribute[],
    differentApi?,
  ): Observable<T> {
    const url = `${this.apiUrl}/${endpoint}`;

    let params = new HttpParams()
      .set('limit', requestParams.limit?.toString())
      .set('page', requestParams.page?.toString())
    if (!!attribute) {
      attribute.forEach(
        (att) => (params = params.append(att.param, att.value))
      );
    }

    const project = localStorage.getItem('project');
    if (project) {
      params = params.append('project', project);
    };

    return this.http.get<T>(url, { params }).pipe(
      map((response: T) => {
        return response;
      })
    );
  }

  genericGetParamsStrict<T>(
    endpoint: string,
    requestParams?: RequestParams,
    attribute?: RequestAttribute[],
    differentApi?,
  ): Observable<T> {
    const url = `${this.apiUrl}/${endpoint}`;

    let params = new HttpParams()
    if (!!attribute) {
      attribute.forEach(
        (att) => (params = params.append(att.param, att.value))
      );
    }

    const project = localStorage.getItem('project');
    if (project) {
      params = params.append('project', project);
    };

    return this.http.get<T>(url, { params }).pipe(
      map((response: T) => {
        return response;
      })
    );
  }

  genericGetParamsNoPage<T>(
    endpoint: string,
    requestParams?: RequestParams,
    attribute?: RequestAttribute[],
    differentApi?,
  ): Observable<T> {
    const url = `${this.apiUrl}/${endpoint}`;
    let params = new HttpParams()
    if (!!attribute) {
      attribute.forEach(
        (att) => (params = params.append(att.param, att.value))
      );
    }

    const project = localStorage.getItem('project');
    if (project) {
      params = params.append('project', project);
    };

    return this.http.get<T>(url, { params }).pipe(
      map((response: T) => {
        return response;
      })
    );
  }

  genericGetListTable<T>(
    endpoint: string,
    requestParams: RequestParams,
    attribute?: RequestAttribute[],
    filterAttribute?: RequestAttribute[]
  ): Observable<T> {
    const url = `${this.apiUrl}/${endpoint}`;
    let params = new HttpParams()
      .set('skip', requestParams.skip?.toString())
      .set('sort', requestParams.sort)
      .set('limit', requestParams.limit?.toString());
    if (!!attribute) {
      attribute.forEach(
        (att) => (params = params.append(att.param, att.value))
      );
    }
    if (!!filterAttribute) {
      filterAttribute.forEach(
        (att) => (params = params.append(att.param, att.value))
      );
    }

    const project = localStorage.getItem('project');
    if (project) {
      params = params.append('project', project);
    };

    return this.http.get<T>(url, { params }).pipe(
      map((response: T) => {
        return response;
      })
    );
  }

  genericPost<T>(
    endpoint: string,
    data: any,
    headers: any,
    attParams?: RequestAttribute[]
  ): Observable<T> {
    const url = `${this.apiUrl}/${endpoint}`;
    let params = new HttpParams();

    const project = localStorage.getItem('project');
    const classification = localStorage.getItem('classification');

    if (project) {
      params = params.append('project', project);
    }
    if (classification) {
      params = params.append('classification', classification);
    }

    if (attParams && attParams.length > 0) {
      attParams.forEach(item => {
        params = params.append(item.param, item.value);
      });
    }

    const httpOptions = {
      headers: new HttpHeaders(headers),
      params
    };

    return this.http.post<T>(url, data, httpOptions).pipe(map((response: T) => response));
  }

  genericPostNoProject<T>(
    endpoint: string,
    data: any,
    headers: any,
    differentApi?,
    attParams?: RequestAttribute[]
  ): Observable<T> {
    const url = `${this.apiUrl}/${endpoint}`;
    let params = new HttpParams()
    if (attParams && attParams.length > 0) {
      attParams.forEach(item => params.append(item.param, item.value))
    }

    const httpOptions = {
      headers: new HttpHeaders(headers),
      params
    };

    return this.http
      .post<T>(url, data, httpOptions)
      .pipe(map((response: T) => response));
  }

  genericPostDownload<T>(
    endpoint: string,
    body: any,
    attribute?: RequestAttribute[]
  ): Observable<T> {
    const url = `${this.apiUrl}/${endpoint}`;
    let params = new HttpParams();

    if (!!attribute) {
      attribute.forEach(
        (att) => (params = params.append(att.param, att.value))
      );
    }
    const project = localStorage.getItem('project');
    if (project) {
      params = params.append('project', project);
    };

    return this.http
      .post(url, body, { responseType: 'arraybuffer', params })
      .pipe((response: any) => {
        return response;
      });
  }

  genericPostDownload2<T>(
    endpoint: string,
    body: any,
    attribute?: RequestAttribute[]
  ): Observable<T> {
    const url = `${this.apiUrl}/${endpoint}`;
    let params = new HttpParams();

    if (!!attribute) {
      attribute.forEach(
        (att) => (params = params.append(att.param, att.value))
      );
    }
    const project = localStorage.getItem('project');
    if (project) {
      params = params.append('project', project);
    };

    return this.http
      .post(url, body, { responseType: 'blob', params })
      .pipe((response: any) => {
        return response;
      });
  }

  genericPostNotData<T>(
    endpoint: string,
    data: any,
    differentApi?
  ): Observable<T> {
    const url = `${this.apiUrl}/${endpoint}`;

    let params = new HttpParams()
    const project = localStorage.getItem('project');
    if (project) {
      params = params.append('project', project);
    };

    return this.http.post<T>(url, data, { params }).pipe(map((response: T) => response));
  }


  genericPostParams<T>(endpoint: string, requestParams: RequestParams, attribute?: RequestAttribute[], data?: any): Observable<T> {
    const params = this.buildHttpParams(requestParams, attribute);
    const url = `${this.apiUrl}/${endpoint}`;
    return this.http.post<T>(url, data, { params }).pipe(map((response: T) => response));
  }

  genericPut<T>(
    endpoint: string,
    data: any,
    id?: string,
  ): Observable<T> {
    const url = id
      ? `${this.apiUrl}/${endpoint}/${id}`
      : `${this.apiUrl}/${endpoint}`;

    let params = new HttpParams();

    return this.http.put<T>(url, data, { params }).pipe(map((response: T) => response));
  }

  genericDelete<T>(endpoint: string, id: string, differentApi?): Observable<T> {
    const api = this.apiUrl;

    const url = id ? `${this.apiUrl}/${endpoint}/${id}` : `${this.apiUrl}/${endpoint}`;

    let params = new HttpParams();
    const project = localStorage.getItem('project');
    if (project) {
      params = params.append('project', project);
    };

    return this.http.delete<T>(url, { params }).pipe(map((response: T) => response));
  }


  genericDeleteNoProject<T>(endpoint: string, id?: string): Observable<T> {
    const url = id ? `${this.apiUrl}/${endpoint}/${id}` : `${this.apiUrl}/${endpoint}`;

    return this.http.delete<T>(url).pipe(map((response: T) => response));
  }

  genericDeleteBody<T>(endpoint: string, data: any, useProject = true): Observable<T> {
    let params = new HttpParams();
    const project = localStorage.getItem('project');
    if (project && useProject) {
      params = params.append('project', project);
    };

    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      body: data,
      params
    };

    const url = `${this.apiUrl}/${endpoint}`;
    return this.http.delete<T>(url, options).pipe(
      map((response: T) => {
        return response;
      })
    );
  }

  genericDeleteAll<T>(
    endpoint: string,
    id: string,
    attribute?: RequestAttribute[]
  ): Observable<T> {
    const url = `${this.apiUrl}/${endpoint}/${id}`;
    let params = new HttpParams();
    if (!!attribute) {
      attribute.forEach(
        (att) => (params = params.append(att.param, att.value))
      );
    }
    const project = localStorage.getItem('project');
    if (project) {
      params = params.append('project', project);
    };
    return this.http.delete<T>(url, { params }).pipe(
      map((response: T) => {
        return response;
      })
    );
  }

  genericPutBody<T>(
    endpoint: string,
    body: any,
    attribute?: RequestAttribute[]
  ): Observable<T> {
    const url = `${this.apiUrl}/${endpoint}`;
    let params = new HttpParams();
    if (!!attribute) {
      attribute.forEach(
        (att) => (params = params.append(att.param, att.value))
      );
    }
    return this.http.put<T>(url, body, { params }).pipe(
      map((response: T) => {
        return response;
      })
    );
  }

  genericGetSelectFiltroScroll<T>(
    urlApi: string,
    endpoint: string,
    requestParams: RequestParams,
    attribute?: RequestAttribute[]
  ): Observable<T> {
    const url = `${urlApi}/${endpoint}`;
    let params = new HttpParams()
      .set('page', requestParams.page.toString())
      .set('sort', requestParams.sort)
      .set('limit', requestParams.limit.toString());
    if (!!attribute) {
      attribute.forEach(
        (att) => (params = params.append(att.param, att.value))
      );
    }
    const project = localStorage.getItem('project');
    if (project) {
      params = params.append('project', project);
    };
    return this.http.get<T>(url, { params }).pipe(
      map((response: T) => {
        return response;
      })
    );
  }
}
